import { useState } from 'react';
import { C } from '../styles/colours.js';
import { supabase } from '../supabase.js';

function getStrength(pwd) {
  if (pwd.length === 0) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { score, label: 'Weak', color: C.red };
  if (score === 2) return { score, label: 'Fair', color: '#f0a500' };
  if (score === 3) return { score, label: 'Good', color: C.gold };
  return { score, label: 'Strong 💪', color: C.green };
}

function Signup({ onBack, onSignup, onLogin }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [type, setType] = useState('personal');
  const [venueName, setVenueName] = useState('');
  const [venueType, setVenueType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const strength = getStrength(password);
  const passwordMatch = confirm.length > 0 && password === confirm;
  const passwordNoMatch = confirm.length > 0 && password !== confirm;

  const inputStyle = {
    flex: 1, background: 'transparent',
    border: 'none', padding: '12px 14px',
    fontSize: 14, color: C.white,
    outline: 'none', fontFamily: 'sans-serif',
  };

  const fieldStyle = {
    display: 'flex', alignItems: 'center',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10, overflow: 'hidden',
  };

  const Label = ({ text }) => (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>{text}</div>
  );

  const VENUE_TYPES = ['🏨 Hotel', '🍽️ Restaurant', '🍺 Bar', '🎊 Wedding', '🎉 Events', '🏢 Corporate', '🎓 Education', '🎯 Other'];

  const handleSignup = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) {
      setError('Please fill in all fields');
      return;
    }
    if (!passwordMatch) {
      setError('Passwords do not match');
      return;
    }
    if (strength.score < 2) {
      setError('Password is too weak');
      return;
    }
    setLoading(true);
    setError('');

    const { data, error: err } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          name,
          account_type: type,
          venue_name: venueName,
          venue_type: venueType,
        }
      }
    });

    setLoading(false);

    if (err) {
      setError(err.message);
      return;
    }

    // Email already registered → Supabase returns a user with empty identities
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setError('This email is already registered. Try logging in instead.');
      return;
    }

    // No session = email confirmation required → show the "check inbox" screen
    if (!data.session) {
      setSent(true);
      return;
    }

    // Session exists = confirmation is off → log straight in
    onSignup(data.user);
  };

  // ===== CHECK YOUR INBOX SCREEN =====
  if (sent) {
    return (
      <div style={{ background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: '20px', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 28 }}>
          <button onClick={onLogin} style={{ background: C.card, border: 'none', color: C.textSub, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>← Back to login</button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>📬</div>
          <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 12 }}>Check your inbox!</div>
          <div style={{ fontSize: 14, color: C.textSub, lineHeight: 1.6, marginBottom: 6 }}>We sent a confirmation link to:</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.gold, marginBottom: 16, wordBreak: 'break-all' }}>{email}</div>
          <div style={{ fontSize: 13, color: C.textSub, lineHeight: 1.6, marginBottom: 24 }}>
            Click the link in the email to activate your account. Don't see it? <strong style={{ color: C.white }}>Check your spam / junk folder</strong> — and mark it "Not junk" so future emails land in your inbox.
          </div>
          <button onClick={onLogin} style={{ width: '100%', background: C.gold, color: '#000', border: 'none', borderRadius: 14, padding: 16, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: '20px', maxWidth: 480, margin: '0 auto' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <button onClick={onBack} style={{ background: C.card, border: 'none', color: C.textSub, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>← Back</button>
        <div style={{ width: 36, height: 36, borderRadius: 9, background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14, color: C.white }}>TQ</div>
      </div>

      <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 4 }}>Create account 🎉</div>
      <div style={{ fontSize: 13, color: C.textSub, marginBottom: 24 }}>Free to start — no card needed</div>

      {error && (
        <div style={{ background: `${C.red}20`, border: `1px solid ${C.red}40`, borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: C.red }}>
          {error}
        </div>
      )}

      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 14, padding: 4, display: 'flex', marginBottom: 24 }}>
        <button onClick={() => setType('personal')} style={{ flex: 1, background: type === 'personal' ? C.gold : 'transparent', color: type === 'personal' ? '#000' : C.textSub, border: 'none', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          👤 Personal
        </button>
        <button onClick={() => setType('business')} style={{ flex: 1, background: type === 'business' ? C.purple : 'transparent', color: type === 'business' ? '#fff' : C.textSub, border: 'none', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          🏢 Venue / Event
        </button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <Label text="FULL NAME" />
        <div style={fieldStyle}>
          <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
        </div>
      </div>

      {type === 'business' && (
        <>
          <div style={{ marginBottom: 14 }}>
            <Label text="VENUE / ORGANISATION NAME" />
            <div style={fieldStyle}>
              <input type="text" placeholder="e.g. The Grand Hotel..." value={venueName} onChange={e => setVenueName(e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <Label text="TYPE OF VENUE" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {VENUE_TYPES.map(v => (
                <button key={v} onClick={() => setVenueType(v)} style={{ background: venueType === v ? 'rgba(155,93,229,0.25)' : 'rgba(155,93,229,0.08)', border: `1px solid ${venueType === v ? C.purple : 'rgba(155,93,229,0.25)'}`, borderRadius: 20, padding: '6px 12px', fontSize: 12, color: venueType === v ? C.white : C.textSub, cursor: 'pointer', fontWeight: venueType === v ? 700 : 400 }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div style={{ marginBottom: 14 }}>
        <Label text="EMAIL" />
        <div style={fieldStyle}>
          <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <div style={{ marginBottom: 6 }}>
        <Label text="PASSWORD" />
        <div style={fieldStyle}>
          <input type={showPwd ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
          <button onClick={() => setShowPwd(!showPwd)} style={{ background: 'none', border: 'none', color: C.textSub, cursor: 'pointer', padding: '0 14px', fontSize: 16 }}>
            {showPwd ? '🙈' : '👁️'}
          </button>
        </div>
      </div>

      {password.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ flex: 1, height: 3, borderRadius: 3, background: i <= strength.score ? strength.color : 'rgba(255,255,255,0.1)', transition: 'background 0.3s' }} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: strength.color, fontWeight: 600 }}>{strength.label}</div>
        </div>
      )}

      <div style={{ marginBottom: 6 }}>
        <Label text="CONFIRM PASSWORD" />
        <div style={{ ...fieldStyle, border: `1px solid ${passwordNoMatch ? C.red : passwordMatch ? C.green : 'rgba(255,255,255,0.12)'}` }}>
          <input type={showConfirm ? 'text' : 'password'} placeholder="••••••••" value={confirm} onChange={e => setConfirm(e.target.value)} style={inputStyle} />
          <button onClick={() => setShowConfirm(!showConfirm)} style={{ background: 'none', border: 'none', color: C.textSub, cursor: 'pointer', padding: '0 14px', fontSize: 16 }}>
            {showConfirm ? '🙈' : '👁️'}
          </button>
        </div>
      </div>

      {confirm.length > 0 && (
        <div style={{ fontSize: 11, color: passwordMatch ? C.green : C.red, marginBottom: 14, fontWeight: 600 }}>
          {passwordMatch ? '✅ Passwords match' : '❌ Passwords do not match'}
        </div>
      )}

      <div style={{ background: 'rgba(255,209,102,0.07)', border: '1px solid rgba(255,209,102,0.2)', borderRadius: 12, padding: '12px 14px', marginBottom: 20, fontSize: 12, color: C.textSub, lineHeight: 1.6 }}>
        🎁 <strong style={{ color: C.gold }}>Free plan:</strong> your first full game is free. Upgrade anytime.
      </div>

      <button onClick={handleSignup} disabled={!passwordMatch || strength.score < 2 || loading}
        style={{ width: '100%', background: passwordMatch && strength.score >= 2 && !loading ? C.gold : 'rgba(255,255,255,0.1)', color: passwordMatch && strength.score >= 2 && !loading ? '#000' : C.textMuted, border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 800, cursor: 'pointer', marginBottom: 16, transition: 'all 0.2s' }}>
        {loading ? 'Creating account...' : 'Create Free Account'}
      </button>

      <div style={{ textAlign: 'center', fontSize: 11, color: C.textMuted, marginBottom: 16 }}>
        By signing up you agree to our{' '}
        <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: C.textSub, fontWeight: 700, textDecoration: 'underline' }}>Terms</a>
        {' '}&amp;{' '}
        <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: C.textSub, fontWeight: 700, textDecoration: 'underline' }}>Privacy Policy</a>
      </div>

      <div style={{ textAlign: 'center', fontSize: 13, color: C.textSub }}>
        Already have an account?{' '}
        <span onClick={onLogin} style={{ color: C.gold, fontWeight: 700, cursor: 'pointer' }}>Log in</span>
      </div>
    </div>
  );
}

export default Signup;
